import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  fetchInsuranceStats,
  fetchEvents,
  type InsuranceStats,
  type InsuranceEvent,
} from '../api/insurance';
import { useInsurancePool, formatAGU } from '../sui';

const DEFAULT_STATS: InsuranceStats = {
  totalProducts: 4,
  totalEvents: 9,
  totalCompensations: 5,
  totalSlashings: 2,
  poolBalance: '3,400',
};

export default function Dashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState<InsuranceStats>(DEFAULT_STATS);
  const [events, setEvents] = useState<InsuranceEvent[]>([]);
  const [loading, setLoading] = useState(true);

  // On-chain insurance pool data
  const { info: poolOnchain } = useInsurancePool();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [s, ev] = await Promise.all([
          fetchInsuranceStats(),
          fetchEvents(),
        ]);
        if (cancelled) return;
        setStats(s);
        setEvents(ev.slice(0, 8));
      } catch {
        /* keep defaults */
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const KPI = [
    { icon: '📦', label: 'Products', value: stats.totalProducts, cls: '' },
    { icon: '⚡', label: 'Events', value: stats.totalEvents, cls: stats.totalEvents > 0 ? 'protected' : '' },
    { icon: '💰', label: 'Compensations', value: stats.totalCompensations, cls: 'safe' },
    { icon: '🔥', label: 'Slashings', value: stats.totalSlashings, cls: stats.totalSlashings > 0 ? 'protected' : 'safe' },
  ];

  const ACTIONS = [
    { icon: '📦', label: 'Products', desc: 'Manage insurance products', path: '/products' },
    { icon: '📋', label: 'File Claim', desc: 'Report an incident', path: '/claims' },
    { icon: '🏊', label: 'Pool Status', desc: 'View pool & slashings', path: '/pool' },
    { icon: '📄', label: 'Documentation', desc: 'Insurance API reference', path: 'https://doc.agentumi.xyz' },
  ];

  const severityBadge = (s: string) => {
    const cls = s === 'critical' || s === 'high' ? 'warning' : s === 'medium' ? 'active' : 'safe';
    return <span className={`insurance-badge ${cls}`}>{s}</span>;
  };

  return (
    <div>
      <header className="insurance-header">
        <h1>Insurance Dashboard</h1>
        <p>AI Agent task failure protection & risk management</p>
      </header>

      {/* ── Pool Balance ── */}
      <div className="insurance-card" style={{ marginBottom: 24, textAlign: 'center' }}>
        <span className="insurance-shield-icon" />
        <div className="insurance-stat-label">Insurance Pool Balance (AGU) — On-chain</div>
        <div className="insurance-stat-value safe" style={{ fontSize: 48 }}>{formatAGU(poolOnchain.poolBalance)}</div>
        <div className="insurance-coverage">
          <div className="insurance-coverage-bar">
            <div className="insurance-coverage-fill" style={{ width: '72%' }} />
          </div>
        </div>
        <div className="insurance-stat-label" style={{ marginTop: 8 }}>
          {poolOnchain.activePolicies} active policies • Premiums: {formatAGU(poolOnchain.totalPremiums)} AGU
        </div>
      </div>

      {/* ── KPI Grid ── */}
      <div className="insurance-grid">
        {KPI.map((k) => (
          <div className="insurance-card" key={k.label}>
            <div className="insurance-card-header">
              <span className="insurance-card-title">
                <span className="insurance-icon">{k.icon}</span>
                {k.label}
              </span>
            </div>
            <div className={`insurance-stat-value ${k.cls}`}>{k.value}</div>
          </div>
        ))}
      </div>

      {/* ── Recent Events ── */}
      <div className="insurance-card" style={{ marginTop: 24, marginBottom: 24 }}>
        <div className="insurance-card-header">
          <span className="insurance-card-title">
            <span className="insurance-icon">⚡</span>
            Recent Events
          </span>
          <span className="insurance-badge active">{loading ? '…' : `${events.length}`}</span>
        </div>
        {loading ? (
          <div className="insurance-loading">Loading…</div>
        ) : events.length === 0 ? (
          <div className="insurance-empty">No events recorded. All agents operating normally.</div>
        ) : (
          <table className="insurance-table">
            <thead>
              <tr>
                <th>Type</th>
                <th>Agent</th>
                <th>Severity</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              {events.map((e, i) => (
                <tr key={i}>
                  <td><span className="insurance-badge active">{e.type}</span></td>
                  <td className="mono">{e.agentId}</td>
                  <td>{severityBadge(e.severity)}</td>
                  <td>{e.description || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* ── Quick Actions ── */}
      <div className="insurance-grid">
        {ACTIONS.map((a) => (
          <div
            className="insurance-card insurance-action-card"
            key={a.label}
            onClick={() =>
              a.path.startsWith('http')
                ? window.open(a.path, '_blank')
                : navigate(a.path)
            }
            style={{ cursor: 'pointer' }}
          >
            <span className="insurance-icon" style={{ fontSize: 32 }}>{a.icon}</span>
            <h3 style={{ marginTop: 12 }}>{a.label}</h3>
            <p className="insurance-stat-label">{a.desc}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
